import { MAX_SALES_BYTES, parseSalesCsv, type SalesGrid } from './salesImport.js';
export type SalesSheet = { sheet: string; data: SalesGrid };

export async function readSalesFile(
  file: File,
  delimiter: string,
  encoding: string,
): Promise<{ sheets: SalesSheet[]; base64: string; sha: string }> {
  if (!file.size || file.size > MAX_SALES_BYTES)
    throw new Error('Carica un file fino a 1 MB. Suddividi gli estratti più grandi.');
  if (!/\.(csv|tsv|xlsx)$/i.test(file.name))
    throw new Error(
      'Formati disponibili: CSV, TSV e XLSX. Per XLS, PDF o foto esporta prima un CSV o XLSX.',
    );
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const sha = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', buffer)), (n) =>
    n.toString(16).padStart(2, '0'),
  ).join('');
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192)
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  let sheets: SalesSheet[];
  if (/\.xlsx$/i.test(file.name)) {
    // Bound advertised ZIP expansion before handing untrusted input to XML parsing.
    const view = new DataView(buffer);
    let expanded = 0;
    let entries = 0;
    for (let i = 0; i + 46 <= view.byteLength; i++)
      if (view.getUint32(i, true) === 0x02014b50) {
        expanded += view.getUint32(i + 24, true);
        entries++;
      }
    if (!entries || entries > 200 || expanded > 20 * 1024 * 1024)
      throw new Error('Archivio Excel troppo complesso o non valido. Esporta un CSV.');
    sheets = await new Promise<SalesSheet[]>((resolve, reject) => {
      const worker = new Worker(new URL('./salesFile.worker.ts', import.meta.url), {
        type: 'module',
      });
      const timer = setTimeout(() => {
        worker.terminate();
        reject(new Error('Lettura Excel troppo lunga. Esporta un CSV.'));
      }, 20000);
      worker.onmessage = (event) => {
        clearTimeout(timer);
        worker.terminate();
        if (event.data.error) reject(new Error(event.data.error));
        else resolve(event.data.sheets);
      };
      worker.onerror = () => {
        clearTimeout(timer);
        worker.terminate();
        reject(new Error('File Excel non leggibile.'));
      };
      worker.postMessage(buffer);
    });
  } else {
    const text = new TextDecoder(encoding, { fatal: true }).decode(bytes);
    sheets = [
      { sheet: 'CSV', data: parseSalesCsv(text, /\.tsv$/i.test(file.name) ? '\t' : delimiter) },
    ];
  }
  return { sheets, base64: btoa(binary), sha };
}
